/**
 * Premium public landing page for SpaceLeadX.AI
 * -------------------------------------------------
 * Audience: French-Canadian service entrepreneurs (terrassement, paysagisme,
 * deneigement, renovation, plomberie, ...).
 *
 * Design system: relies on the Dark Premium tokens defined in index.css
 * (slate-950 background, electric blue accent #3B82F6, Manrope headings).
 *
 * This page is intentionally self-contained: it imports only existing UI
 * primitives (Button, Card, Badge) and renders a hero + features + pricing
 * + testimonial + CTA + footer. No external assets required beyond the
 * already-loaded Google Fonts.
 */

import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LanguageToggle } from '@/components/ui/LanguageSelector';
import { PLANS, PLAN_ORDER } from '@/config/plans';
import { billingAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles, ArrowRight, Phone, MessageSquare, Bot, MapPin,
  Calendar, HardHat, Check, Star, ShieldCheck, Zap, ChevronRight,
  Menu, X, Brain, Building2,
} from 'lucide-react';


// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function PublicHeader({ tf, isAuthenticated }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { href: '#features', label: tf('Fonctionnalites', 'Features') },
    { href: '#how', label: tf('Comment ca marche', 'How it works') },
    { href: '#pricing', label: tf('Tarifs', 'Pricing') },
    { href: '#testimonials', label: tf('Temoignages', 'Reviews') },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/85 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
      data-testid="landing-header"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl md:text-2xl font-black tracking-tightish">
          Space<span className="text-primary">LeadX</span>
          <span className="text-primary/60">.AI</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:block">
            <LanguageToggle />
          </div>
          {isAuthenticated ? (
            <Button asChild size="sm" data-testid="header-dashboard-btn">
              <Link to="/dashboard">
                {tf('Mon tableau de bord', 'My dashboard')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="header-login-btn">
                <Link to="/login">{tf('Se connecter', 'Sign in')}</Link>
              </Button>
              <Button asChild size="sm" data-testid="header-cta-btn">
                <Link to="/signup">
                  {tf('Activer mon espace', 'Activate my space')}
                </Link>
              </Button>
            </>
          )}
          <button
            type="button"
            className="md:hidden p-2 text-foreground"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-white/5 bg-background/95 backdrop-blur-xl">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-muted-foreground hover:bg-white/5 hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2">
              <LanguageToggle />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}


// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function Hero({ tf, isAuthenticated }) {
  return (
    <section className="relative pt-28 md:pt-36 pb-16 md:pb-28 overflow-hidden" data-testid="landing-hero">
      {/* Radial glow background */}
      <div
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 0%, rgba(59, 130, 246, 0.22) 0%, rgba(59, 130, 246, 0.06) 35%, transparent 70%)',
        }}
      />
      {/* Grid pattern */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at top, black 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 md:px-8 text-center">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur text-xs md:text-sm">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          <span className="text-muted-foreground">
            {tf('Pour les entrepreneurs de terrain au Quebec', 'For field entrepreneurs in Quebec')}
          </span>
        </div>

        {/* Headline */}
        <h1 className="mt-6 md:mt-8 font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter2 leading-[1.05] max-w-5xl mx-auto">
          {tf(
            <>
              Le cerveau qui gere<br className="hidden sm:block" />{' '}
              <span className="bg-gradient-to-r from-blue-400 via-primary to-cyan-400 bg-clip-text text-transparent">
                vos clients
              </span>{' '}
              pendant que vous gerez{' '}
              <span className="text-foreground">vos chantiers</span>.
            </>,
            <>
              The brain that runs<br className="hidden sm:block" />{' '}
              <span className="bg-gradient-to-r from-blue-400 via-primary to-cyan-400 bg-clip-text text-transparent">
                your clients
              </span>{' '}
              while you run{' '}
              <span className="text-foreground">your job sites</span>.
            </>
          )}
        </h1>

        {/* Sub-headline */}
        <p className="mt-6 mx-auto max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
          {tf(
            "SpaceLeadX.AI repond a vos appels, qualifie vos prospects, envoie les SMS de rappel et vous prepare la journee — pendant que vous, vous travaillez dehors.",
            "SpaceLeadX.AI answers your calls, qualifies your leads, sends SMS reminders and preps your day — while you're out there working."
          )}
        </p>

        {/* CTAs */}
        <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          {isAuthenticated ? (
            <Button asChild size="lg" className="h-12 md:h-14 px-7 text-base font-bold" data-testid="hero-dashboard-btn">
              <Link to="/dashboard">
                {tf('Aller a mon bureau', 'Go to my office')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          ) : (
            <>
              <Button asChild size="lg" className="h-12 md:h-14 px-7 text-base font-bold" data-testid="hero-primary-cta">
                <Link to="/signup">
                  <Sparkles className="mr-2 h-5 w-5" />
                  {tf('Demarrer avec SpaceLeadX', 'Get started with SpaceLeadX')}
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 md:h-14 px-7 text-base font-bold"
                data-testid="hero-secondary-cta"
              >
                <a href="#pricing">
                  {tf('Demander une demo', 'Book a demo')}
                  <ChevronRight className="ml-1 h-5 w-5" />
                </a>
              </Button>
            </>
          )}
        </div>

        {/* Trust strip */}
        <div className="mt-12 md:mt-16 flex flex-col items-center gap-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              {tf('Conforme Loi 25', 'Quebec Law 25 compliant')}
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-amber-400" />
              {tf('Setup en 5 minutes', '5-minute setup')}
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              {tf('IA + Support humain disponible', 'AI + human support included')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

function Features({ tf }) {
  const features = [
    {
      icon: Phone,
      title: tf('Receptionniste IA', 'AI Receptionist'),
      desc: tf(
        'Repond a vos appels manques, prend les coordonnees et vous envoie un SMS quand le prospect est qualifie.',
        'Answers your missed calls, captures contact info and texts you when a lead is qualified.'
      ),
      tint: 'from-blue-500/25 to-cyan-500/10',
      comingSoon: true,
    },
    {
      icon: MessageSquare,
      title: tf('SMS automatique', 'Auto SMS'),
      desc: tf(
        'Confirmations de rendez-vous, rappels la veille, suivis 72h apres — tout est automatique.',
        'Appointment confirmations, day-before reminders, 72h follow-ups — all automatic.'
      ),
      tint: 'from-purple-500/25 to-pink-500/10',
    },
    {
      icon: Bot,
      title: tf('Marketing IA & SEO', 'AI Marketing & SEO'),
      desc: tf(
        'Analyse vos concurrents, detecte les opportunites et redige vos articles pour Google.',
        'Analyzes competitors, surfaces opportunities and writes your articles for Google.'
      ),
      tint: 'from-amber-500/25 to-orange-500/10',
    },
    {
      icon: MapPin,
      title: tf('Google My Business', 'Google My Business'),
      desc: tf(
        'Repond aux avis, publie vos posts hebdo et garde votre fiche fraiche sans y penser.',
        'Replies to reviews, publishes weekly posts and keeps your profile fresh on autopilot.'
      ),
      tint: 'from-emerald-500/25 to-teal-500/10',
    },
    {
      icon: HardHat,
      title: tf('Equipe sur le terrain', 'Field crew'),
      desc: tf(
        'Vos employes pointent leur quart depuis leur cellulaire, geolocalisation et photos incluses.',
        'Your crew clocks in from their phone with geolocation and photos included.'
      ),
      tint: 'from-rose-500/25 to-red-500/10',
    },
    {
      icon: Calendar,
      title: tf('CRM & Pipeline', 'CRM & Pipeline'),
      desc: tf(
        'Vos prospects, soumissions et clients dans une seule vue. Plus de devis perdu dans un courriel.',
        'Leads, quotes and clients in a single view. No more bid lost in your inbox.'
      ),
      tint: 'from-violet-500/25 to-indigo-500/10',
    },
  ];

  return (
    <section id="features" className="py-20 md:py-32" data-testid="landing-features">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
            {tf('Fonctionnalites', 'Features')}
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter2 leading-tight">
            {tf(
              <>
                Tout votre bureau, <span className="text-muted-foreground">en six modules.</span>
              </>,
              <>
                Your whole back-office, <span className="text-muted-foreground">in six modules.</span>
              </>
            )}
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-xl">
            {tf(
              "Concu pour les entrepreneurs qui passent leur journee sur le terrain, pas devant un ecran. IA + Support humain disponible.",
              'Built for entrepreneurs who spend their day outside, not behind a screen. AI + human support included.'
            )}
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <Card key={f.title} className="group relative overflow-hidden" data-testid={`feature-card-${f.title}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${f.tint} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <CardContent className="relative p-6 md:p-7">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    {f.comingSoon && (
                      <Badge
                        className="bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30"
                        data-testid="badge-coming-soon"
                      >
                        {tf('Bientot disponible', 'Coming soon')}
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-bold tracking-tightish">{f.title}</h3>
                  <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// How it works
// ---------------------------------------------------------------------------

function HowItWorks({ tf }) {
  const steps = [
    {
      icon: Building2,
      title: tf('Branchez votre entreprise', 'Plug in your business'),
      desc: tf(
        'Nom, telephone, services, zones desservies. Ca prend 5 minutes.',
        'Name, phone, services, service areas. Takes 5 minutes.'
      ),
    },
    {
      icon: Brain,
      title: tf('Laissez l\'IA travailler', 'Let the AI work'),
      desc: tf(
        'Elle repond aux appels, qualifie les prospects et envoie les premieres reponses.',
        'It answers calls, qualifies leads and fires the first replies.'
      ),
    },
    {
      icon: HardHat,
      title: tf('Concentrez-vous sur vos chantiers', 'Focus on your job sites'),
      desc: tf(
        'Vous recevez un resume des nouveaux clients chaque matin. Tout est deja prepare.',
        'You get a brief on the new clients every morning. Everything is already prepped.'
      ),
    },
  ];

  return (
    <section id="how" className="py-20 md:py-32 border-t border-white/5" data-testid="landing-how">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
            {tf('En trois etapes', 'Three steps')}
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter2 leading-tight">
            {tf('Branche. IA. Liberte.', 'Plug. AI. Freedom.')}
          </h2>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="relative" data-testid={`how-step-${i + 1}`}>
                <div className="absolute -top-2 -left-2 text-6xl md:text-7xl font-heading font-black text-white/5 select-none leading-none">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <Card className="relative h-full">
                  <CardContent className="p-6 md:p-7">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-bold tracking-tightish">{s.title}</h3>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">{s.desc}</p>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

function Pricing({ tf, language, isAuthenticated }) {
  const navigate = useNavigate();
  const [loadingPid, setLoadingPid] = useState(null);

  // Public pricing CTA — when authenticated, hit Stripe Checkout directly
  // instead of redirecting to /dashboard. When NOT authenticated, send users
  // to the self-serve signup with the picked plan pre-selected via ?plan=…
  // Enterprise still goes through signup (no self-serve checkout for now).
  const handlePlanClick = async (pid) => {
    if (!isAuthenticated) {
      navigate(`/signup?plan=${encodeURIComponent(pid)}`);
      return;
    }
    if (pid === 'enterprise') {
      navigate('/dashboard');
      return;
    }
    setLoadingPid(pid);
    try {
      const { data } = await billingAPI.createCheckout({ plan_id: pid, interval: 'monthly' });
      if (data && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error(tf('Erreur paiement.', 'Payment error.'));
    } catch (e) {
      const detail = e?.response?.data?.detail || (language === 'fr' ? 'Erreur paiement.' : 'Payment error.');
      toast.error(detail);
    } finally {
      setLoadingPid(null);
    }
  };

  return (
    <section id="pricing" className="py-20 md:py-32 border-t border-white/5" data-testid="landing-pricing">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="max-w-3xl text-center mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
            {tf('Tarifs', 'Pricing')}
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter2 leading-tight">
            {tf('Choisissez votre cadence.', 'Pick your pace.')}
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            {tf(
              'Trois forfaits clairs en dollars canadiens. IA + Support humain inclus a tous les niveaux. Annulez en tout temps.',
              'Three clear plans in Canadian dollars. AI + human support included at every tier. Cancel anytime.'
            )}
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {PLAN_ORDER.map((pid) => {
            const p = PLANS[pid];
            const highlighted = pid === 'pro';
            const features = (p.display_features?.[language]) || p.display_features?.fr || [];
            const suffix = (p.price_suffix?.[language]) || p.price_suffix?.fr || tf('/mois', '/month');
            return (
              <Card
                key={pid}
                className={`relative ${
                  highlighted
                    ? 'border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent shadow-glow-blue'
                    : ''
                }`}
                data-testid={`pricing-card-${pid}`}
              >
                {highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground border-0 px-3 py-1">
                      {tf('Recommande', 'Recommended')}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 md:p-8 space-y-5">
                  <div>
                    <h3 className="font-heading text-2xl font-black tracking-tightish">{p.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {p.tagline[language] || p.tagline.fr}
                    </p>
                  </div>

                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-4xl md:text-5xl font-black tracking-tighter2">
                      {p.price_monthly}$
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {p.currency} {suffix}
                    </span>
                  </div>

                  <ul className="space-y-2.5 text-sm" data-testid={`pricing-features-${pid}`}>
                    {features.map((label) => (
                      <li key={label} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={highlighted ? 'default' : 'outline'}
                    className="w-full h-11"
                    data-testid={`pricing-cta-${pid}`}
                    disabled={loadingPid === pid}
                    onClick={() => handlePlanClick(pid)}
                  >
                    {loadingPid === pid
                      ? tf('Chargement…', 'Loading…')
                      : (pid === 'enterprise'
                        ? tf('Demander une demo', 'Book a demo')
                        : tf(`Choisir ${p.name}`, `Choose ${p.name}`))}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Testimonial
// ---------------------------------------------------------------------------

function Testimonial({ tf }) {
  return (
    <section id="testimonials" className="py-20 md:py-32 border-t border-white/5" data-testid="landing-testimonials">
      <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
        <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/10">
          {tf('Temoignage', 'Testimonial')}
        </Badge>
        <div className="mx-auto flex justify-center gap-1 mb-6">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
          ))}
        </div>
        <blockquote className="font-heading text-2xl md:text-4xl font-black tracking-tighter2 leading-tight">
          {tf(
            <>"Avant SpaceLeadX, je perdais 3 ou 4 appels par semaine.<br className="hidden md:block" /> Aujourd'hui, je ne perds plus rien — et je dors mieux."</>,
            <>"Before SpaceLeadX, I missed 3-4 calls a week.<br className="hidden md:block" /> Now I miss nothing — and I sleep better."</>
          )}
        </blockquote>
        <div className="mt-8 flex items-center justify-center gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/40 to-primary/10 border border-white/10 flex items-center justify-center font-heading font-black text-lg">
            S
          </div>
          <div className="text-left">
            <p className="font-bold">Stef P.</p>
            <p className="text-sm text-muted-foreground">{tf('Entrepreneur en terrassement, Quebec', 'Excavation contractor, Quebec')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// Final CTA + Footer
// ---------------------------------------------------------------------------

function FinalCta({ tf, isAuthenticated }) {
  return (
    <section className="py-20 md:py-32 border-t border-white/5" data-testid="landing-final-cta">
      <div className="mx-auto max-w-4xl px-4 md:px-8">
        <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent">
          <div
            className="absolute inset-0 opacity-50"
            style={{
              background:
                'radial-gradient(60% 80% at 50% 0%, rgba(59,130,246,0.25) 0%, transparent 70%)',
            }}
          />
          <CardContent className="relative p-8 md:p-14 text-center space-y-6">
            <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter2 leading-tight max-w-2xl mx-auto">
              {tf(
                "Pret a laisser l'IA tenir votre bureau ?",
                'Ready to let the AI run your office?'
              )}
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
              {tf(
                'Activez votre espace SpaceLeadX en 5 minutes. IA + Support humain disponible.',
                'Activate your SpaceLeadX space in 5 minutes. AI + human support included.'
              )}
            </p>
            <Button
              asChild
              size="lg"
              className="h-12 md:h-14 px-8 text-base font-bold"
              data-testid="final-cta-btn"
            >
              <Link to={isAuthenticated ? '/dashboard' : '/signup'}>
                <Sparkles className="mr-2 h-5 w-5" />
                {isAuthenticated
                  ? tf('Aller a mon bureau', 'Go to my office')
                  : tf('Activer mon espace', 'Activate my space')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function Footer({ tf, language }) {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/5 py-10 md:py-14" data-testid="landing-footer">
      <div className="mx-auto max-w-7xl px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <Link to="/" className="font-heading text-lg font-black tracking-tightish">
            Space<span className="text-primary">LeadX</span>
            <span className="text-primary/60">.AI</span>
          </Link>
          <p className="mt-2 text-xs text-muted-foreground">
            {tf(`© ${year} SpaceLeadX. Fait au Quebec.`, `© ${year} SpaceLeadX. Made in Quebec.`)}
          </p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
            {tf('Fonctionnalites', 'Features')}
          </a>
          <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">
            {tf('Tarifs', 'Pricing')}
          </a>
          <Link
            to={language === 'fr' ? '/fr/conditions-dutilisation' : '/en/terms-of-use'}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {tf("Conditions d'utilisation", 'Terms of use')}
          </Link>
          <Link
            to={language === 'fr' ? '/fr/politique-de-confidentialite' : '/en/privacy-policy'}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {tf('Confidentialite', 'Privacy')}
          </Link>
        </div>
      </div>
    </footer>
  );
}


// ---------------------------------------------------------------------------
// Page entry
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const tf = (fr, en) => (language === 'fr' ? fr : en);
  const isAuthenticated = !!user;

  return (
    <div className="min-h-screen bg-background text-foreground antialiased" data-testid="landing-page">
      <PublicHeader tf={tf} isAuthenticated={isAuthenticated} />
      <main>
        <Hero tf={tf} isAuthenticated={isAuthenticated} />
        <Features tf={tf} />
        <HowItWorks tf={tf} />
        <Pricing tf={tf} language={language} isAuthenticated={isAuthenticated} />
        <Testimonial tf={tf} />
        <FinalCta tf={tf} isAuthenticated={isAuthenticated} />
      </main>
      <Footer tf={tf} language={language} />
    </div>
  );
}
